import { useMemo } from "react";
import { useI18nContext } from "@hopper-b2b/i18n";
import type { IFlightCardProps } from "@hopper-b2b/types";
import {
  FlightCardFields,
  Airline,
  StopSummary,
  FlightDuration,
  AlgomerchTags,
  ItemPrice,
  Divider,
} from "@hopper-b2b/ui";
import { removeTimezone } from "@hopper-b2b/utilities";
import { DateTimeFormatStyle, formatDateTime } from "@hopper-i18n/formatter";
import clsx from "clsx";
import { useEnhancedEarnAmount } from "../../../hooks/shared";
import { EnhancedEarnRow } from "../../EnhancedEarn";
import styles from "./styles.module.scss";

export const NubankFlightCard = (props: IFlightCardProps) => {
  const {
    duration,
    currentPriceText,
    departureTime,
    arrivalTime,
    airports,
    brandName,
    primaryCarrier,
    airlineName,
    tags,
    tagsLimit,
    onClickTag,
    fiat,
    originCode,
    destinationCode,
    additionalInfo,
    isRoundTrip,
  } = props;

  const { t, language: locale } = useI18nContext();
  const { layoverDuration } = additionalInfo;

  const { isEnhancedEarnEnabled } = useEnhancedEarnAmount();

  const departureTimeFormatted = useMemo(
    () =>
      formatDateTime(
        new Date(removeTimezone(departureTime)),
        locale,
        DateTimeFormatStyle.ShortTime
      ),
    [departureTime, locale]
  );

  const arrivalTimeFormatted = useMemo(
    () =>
      formatDateTime(
        new Date(removeTimezone(arrivalTime)),
        locale,
        DateTimeFormatStyle.ShortTime
      ),
    [arrivalTime, locale]
  );

  const description = useMemo(
    () =>
      isRoundTrip
        ? t("totalRoundTripPerTravelerIncludingTaxes")
        : t("totalOneWayPerTravelerIncludingTaxes"),
    [isRoundTrip, t]
  );

  return (
    <FlightCardFields className={styles.nubankFlightCard}>
      {/* Uncomment to see hackerfares more easily when running locally */}
      {/* {props.fareMultiTicketType === "hackerFare" ? "HACKERFARE" : null} */}
      <div className="flight-card-top-row tags">
        {tags.length > 0 ? (
          <AlgomerchTags
            tags={tags}
            tagsLimit={tagsLimit}
            onClickTag={onClickTag}
          />
        ) : (
          // intentional filler div to keep the chevron in line
          <div></div>
        )}
      </div>
      <div className="flight-card-second-row">
        <div className="flight-details">
          <Airline
            className="flight-details-icon"
            code={primaryCarrier}
            name={airlineName}
            iconOnly
          />
          <div className="flight-details-content">
            <div className="airline-info">
              <p className="airline-name">{airlineName}</p>
              {brandName ? (
                <>
                  <div className="separator"></div>
                  <p className="fare-brand">{brandName}</p>{" "}
                </>
              ) : null}
            </div>
            <div className="duration-info">
              <FlightDuration duration={duration} />
            </div>
          </div>
        </div>
      </div>
      <div className="flight-card-middle-row">
        <div className="flight-card-timing">
          <div className="flight-card-timing-details departure">
            <span className="flight-card-timing-time">
              {departureTimeFormatted}
            </span>
            <span className="flight-card-timing-airline-code">
              {originCode}
            </span>
          </div>
          <div className="flight-card-middle-middle">
            <div className="flight-card-timing-bar">
              {airports?.length > 0 ? (
                <div
                  className={clsx("stops", {
                    oneStop: airports?.length === 1,
                    multipleStops: airports?.length > 1,
                  })}
                ></div>
              ) : null}
            </div>
            <div className="flight-details-time-content-row">
              <StopSummary
                airports={airports}
                separator=":"
                maxAirports={3}
                layoverDuration={
                  airports?.length === 1 ? layoverDuration : null
                }
              />
            </div>
          </div>
          <div className="flight-card-timing-details arrival">
            <span className="flight-card-timing-time">
              {arrivalTimeFormatted}
            </span>
            <span className="flight-card-timing-airline-code">
              {destinationCode}
            </span>
          </div>
        </div>
      </div>
      <Divider />
      <div
        className={clsx("flight-card-bottom-row", {
          hidden: additionalInfo?.isExpanded,
        })}
      >
        <ItemPrice current={currentPriceText} />
        <span className="trip-total-label">{description}</span>
        {isEnhancedEarnEnabled ? (
          <EnhancedEarnRow totalPrice={fiat.value} />
        ) : null}
      </div>
    </FlightCardFields>
  );
};
